import { Box, Text, Textarea, Tabs } from "@mantine/core";
import { translateStepType } from "@utils/flowTypeTranslations";
import { useTranslation } from "@hooks/useTranslation";
import type { FlowStepFormData, AgentExecutionFormData } from "@utils/builders/flowConfig";
import type { ProviderViewModel } from "@models/provider";
import type { ModelViewModel, ModelBasicInfoViewModel } from "@models/model";
import { LLMConfigTab } from "../../LLMConfigTab/LLMConfigTab";
import { OCRConfigTab } from "../../OCRConfigTab/OCRConfigTab";
import { AgentExecutionsEditor } from "../../AgentExecutionsEditor";
import "./StepConfiguration.scss";

interface StepConfigurationProps {
    step: FlowStepFormData;
    stepNumber: number;
    activeTab: string;
    providers: ProviderViewModel[];
    models: ModelBasicInfoViewModel[];
    selectedProvider: ProviderViewModel | null;
    selectedModel: ModelViewModel | null;
    isLoadingModels: boolean;
    isLoadingProviders: boolean;
    onTabChange: (tab: string) => void;
    onProviderChange: (providerId: string) => void;
    onModelChange: (modelName: string) => void;
    onFieldChange: (field: string, value: string | Record<string, unknown> | null) => void;
    llmProviders?: ProviderViewModel[];
    llmModels?: Record<string, ModelBasicInfoViewModel[]>;
    onAgentsChange?: (agents: AgentExecutionFormData[]) => void;
    onLoadAgentModels?: (providerKey: string, providerType: "ocr" | "llm") => Promise<void>;
}

export const StepConfiguration = ({
    step,
    stepNumber,
    activeTab,
    providers,
    models,
    selectedProvider,
    selectedModel,
    isLoadingModels,
    isLoadingProviders,
    onTabChange,
    onProviderChange,
    onModelChange,
    onFieldChange,
    llmProviders = [],
    llmModels = {},
    onAgentsChange,
    onLoadAgentModels,
}: StepConfigurationProps) => {
    const { t } = useTranslation();

    const isLLMStep = step.llmStepConfig !== undefined;
    const isOCRStep = step.ocrStepConfig !== undefined;
    const hasAgents = Array.isArray(step.llmStepConfig?.executions);

    const handleConfigOptionChange = (field: string, value: number | string) => {
        const currentOptions = step.llmStepConfig?.configOptions || {};
        const numValue = typeof value === "number" ? value : value ? parseFloat(value) : null;
        onFieldChange("configOptions", {
            ...currentOptions,
            [field]: numValue,
        });
    };

    const handleOcrOptionsChange = (value: string) => {
        try {
            const parsed = value ? JSON.parse(value) : null;
            onFieldChange("ocrOptions", parsed);
        } catch (error) {
            console.error("Invalid JSON for OCR options:", error);
        }
    };

    const tabLabels = [
        t("admin.flowConfigs.labels.llmConfig"),
        t("admin.flowConfigs.labels.systemPrompt"),
        t("admin.flowConfigs.labels.userPrompt"),
        t("admin.flowConfigs.labels.responseSchema"),
    ];

    return (
        <Box className="step-configuration">
            <Text fw={600} size="lg" className="step-configuration__title">
                Configure Step {stepNumber}: {translateStepType(step.stepType, t)}
            </Text>

            {isLLMStep && (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(value) => onTabChange(value || "0")}
                        className="step-configuration__tabs"
                    >
                        <Tabs.List>
                            {tabLabels.map((label, index) => (
                                <Tabs.Tab key={index} value={String(index)}>
                                    {label}
                                </Tabs.Tab>
                            ))}
                            {hasAgents && onAgentsChange && onLoadAgentModels && (
                                <Tabs.Tab value="agents">
                                    {t("admin.flowConfigs.agents.title")} ({(step.llmStepConfig?.executions || []).length})
                                </Tabs.Tab>
                            )}
                        </Tabs.List>

                        <Tabs.Panel value="0" className="step-configuration__tab-panel">
                            <LLMConfigTab
                                isLoadingProviders={isLoadingProviders}
                                isLoadingModels={isLoadingModels}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider?.id || ""}
                                selectedModel={selectedModel}
                                temperature={step.llmStepConfig?.configOptions?.temperature ?? ""}
                                topP={step.llmStepConfig?.configOptions?.topP ?? ""}
                                topK={step.llmStepConfig?.configOptions?.topK ?? ""}
                                maxOutputTokens={step.llmStepConfig?.configOptions?.maxOutputTokens ?? ""}
                                thinkingBudget={step.llmStepConfig?.configOptions?.thinkingBudget ?? ""}
                                seed={step.llmStepConfig?.configOptions?.seed ?? ""}
                                onProviderChange={onProviderChange}
                                onModelChange={onModelChange}
                                onTemperatureChange={(v) => handleConfigOptionChange('temperature', v)}
                                onTopPChange={(v) => handleConfigOptionChange('topP', v)}
                                onTopKChange={(v) => handleConfigOptionChange('topK', v)}
                                onMaxOutputTokensChange={(v) => handleConfigOptionChange('maxOutputTokens', v)}
                                onThinkingBudgetChange={(v) => handleConfigOptionChange('thinkingBudget', v)}
                                onSeedChange={(v) => handleConfigOptionChange('seed', v)}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="1" className="step-configuration__tab-panel">
                            <Textarea
                                label={t("admin.flowConfigs.labels.systemPrompt")}
                                value={step.llmStepConfig?.systemPrompt || ""}
                                onChange={(e) => onFieldChange("systemPrompt", e.currentTarget.value)}
                                required
                                minRows={30}
                                className="step-configuration__prompt-field"
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="2" className="step-configuration__tab-panel">
                            <Textarea
                                label={t("admin.flowConfigs.labels.userPrompt")}
                                value={step.llmStepConfig?.userPrompt || ""}
                                onChange={(e) => onFieldChange("userPrompt", e.currentTarget.value)}
                                required
                                minRows={30}
                                className="step-configuration__prompt-field"
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="3" className="step-configuration__tab-panel">
                            <Textarea
                                label={t("admin.flowConfigs.labels.responseSchema") + " (Optional)"}
                                value={
                                    step.llmStepConfig?.responseSchema
                                        ? JSON.stringify(step.llmStepConfig.responseSchema, null, 2)
                                        : ""
                                }
                                onChange={(e) => {
                                    try {
                                        const parsed = e.currentTarget.value ? JSON.parse(e.currentTarget.value) : null;
                                        onFieldChange("responseSchema", parsed);
                                    } catch {
                                        // ignore invalid JSON while typing
                                    }
                                }}
                                minRows={30}
                                placeholder='{"type": "object", "properties": {...}}'
                                className="step-configuration__prompt-field"
                            />
                        </Tabs.Panel>

                        {hasAgents && onAgentsChange && onLoadAgentModels && (
                            <Tabs.Panel value="agents" className="step-configuration__tab-panel">
                                <AgentExecutionsEditor
                                    agents={step.llmStepConfig?.executions || []}
                                    llmProviders={llmProviders}
                                    llmModels={llmModels}
                                    onAgentsChange={onAgentsChange}
                                    onLoadModels={onLoadAgentModels}
                                />
                            </Tabs.Panel>
                        )}
                    </Tabs>
                </>
            )}

            {isOCRStep && (
                <OCRConfigTab
                    isLoadingProviders={isLoadingProviders}
                    isLoadingModels={isLoadingModels}
                    providers={providers}
                    models={models}
                    selectedProvider={selectedProvider?.id || ""}
                    selectedModel={selectedModel}
                    ocrOptions={
                        step.ocrStepConfig?.ocrOptions
                            ? JSON.stringify(step.ocrStepConfig.ocrOptions, null, 2)
                            : ""
                    }
                    onProviderChange={onProviderChange}
                    onModelChange={onModelChange}
                    onOcrOptionsChange={handleOcrOptionsChange}
                />
            )}
        </Box>
    );
};
